<template>
  <div class="lobby">
    <h1 class="lobby-title">{{ t('lobby.title') }}</h1>
    <router-link v-if="standing" to="/rank" class="lobby-rank">
      <RankBadge
        :league="standing.league"
        :division="standing.division"
        :placement-remaining="standing.placementRemaining"
        :size="56"
      />
      <p>{{ lobbyRankLabel }}</p>
    </router-link>

    <div class="deck-line">
      <label class="deck-pick">
        <span>{{ t('lobby.playDeck') }}</span>
        <select
          v-if="deckOptions.length > 0"
          class="deck-select"
          :value="activeDeckId"
          :aria-label="t('lobby.playDeck')"
          @change="onActiveDeckChange"
        >
          <option
            v-for="opt in deckOptions"
            :key="opt.id"
            :value="opt.id"
            :disabled="!opt.playable && opt.id !== activeDeckId"
          >
            {{ opt.name }}{{ opt.playable ? '' : t('lobby.deckNotReady') }}
          </option>
        </select>
        <span v-else>{{ t('deck.buildForty') }}</span>
      </label>
      <router-link to="/deck-builder" class="deck-edit">
        {{
          deckOptions.some((opt) => opt.playable)
            ? t('lobby.edit')
            : t('lobby.buildDeck')
        }}
      </router-link>
    </div>

    <TournamentStrip />

    <div class="actions">
      <button
        type="button"
        class="btn-primary"
        :disabled="ranked.closed"
        @click="quickMatch"
      >
        <span>{{ t('lobby.quickMatch') }}</span>
        <span v-if="!ranked.closed && ranked.waiting !== null" class="btn-queue">
          {{ t('lobby.queueWaiting', { n: ranked.waiting }) }}
        </span>
      </button>
      <p v-if="!ranked.closed && duelPrefs.rankedPrompts && nudgeCoins > 0 && ranked.offerPending > 0" class="lobby-hint lobby-offer">
        {{ t('lobby.rankedOffer', { n: nudgeCoins }) }}
      </p>
      <p class="lobby-hint">
        {{ ranked.closed ? t('lobby.queueClosed') : t('lobby.rankedHint') }}
      </p>
      <button
        type="button"
        class="btn-casual"
        :disabled="ranked.closed"
        @click="casualMatch"
      >
        <span>{{ t('lobby.casual') }}</span>
        <span v-if="!ranked.closed && ranked.casualWaiting !== null" class="btn-queue">
          {{ t('lobby.queueWaiting', { n: ranked.casualWaiting }) }}
        </span>
      </button>
      <p class="lobby-hint">
        {{ ranked.closed ? t('lobby.queueClosed') : t('lobby.casualHint') }}
      </p>
      <button
        v-if="site.eventQueue"
        type="button"
        class="btn-casual"
        @click="eventMatch"
      >
        <span>{{ t(`eventQueue.modes.${site.eventQueue.mode}`) }}</span>
        <span v-if="ranked.eventWaiting !== null" class="btn-queue">
          {{ t('lobby.queueWaiting', { n: ranked.eventWaiting }) }}
        </span>
      </button>
      <p v-if="site.eventQueue" class="lobby-hint">
        {{
          site.eventCountdown
            ? t('eventQueue.endsIn', { time: site.eventCountdown })
            : t('lobby.eventHint')
        }}
      </p>
      <router-link to="/rooms" class="btn-secondary">
        {{ t('lobby.privateRooms') }}
      </router-link>
      <p class="lobby-hint">{{ t('lobby.privateHint') }}</p>
      <router-link to="/watch" class="btn-secondary">
        {{ t('lobby.watch') }}
      </router-link>
      <p class="lobby-hint">{{ t('lobby.watchHint') }}</p>
    </div>

    <router-link to="/" class="lobby-back">
      {{ t('lobby.back') }}
    </router-link>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { useRouter } from 'vue-router'
import { activateFirstPlayable, listDeckPlayOptions } from '../game/localDeck'
import { useDeckStore } from '../stores/deck'
import { DuelSfx } from '../game/audio/DuelSfx'
import { armDuelLandscape } from '../composables/useDuelOrientation'
import { useI18n } from '../i18n/t'
import { usePlayerStore } from '../stores/player'
import { useAuthStore } from '../stores/auth'
import RankBadge from '../components/RankBadge.vue'
import TournamentStrip from '../components/TournamentStrip.vue'
import { fetchMyRating } from '../api/rating'
import { useRankedLobbyStore } from '../stores/rankedLobby'
import { useSiteStore } from '../stores/site'
import { useDuelPrefsStore } from '../stores/duelPrefs'
import { RANKED_NUDGE_COINS, type RankedStanding } from '@shared/index'

const { t } = useI18n()
const router = useRouter()
const player = usePlayerStore()
const auth = useAuthStore()
const deckStore = useDeckStore()
const ranked = useRankedLobbyStore()
const site = useSiteStore()
const duelPrefs = useDuelPrefsStore()
const nudgeCoins = RANKED_NUDGE_COINS
activateFirstPlayable()
const deckOptions = computed(() => listDeckPlayOptions())
const activeDeckId = computed(() => deckStore.activeId || '')
const standing = ref<RankedStanding | null>(null)

const lobbyRankLabel = computed(() => {
  const s = standing.value
  if (!s) return ''
  if (s.placementRemaining > 0 || !s.league) {
    return t('rank.placementLeft', { n: s.placementRemaining })
  }
  const name = t(`rank.leagues.${s.league}`)
  if (!s.division || s.league === 'king') return name
  return `${name} ${s.division === 1 ? 'I' : 'II'}`
})

onMounted(() => {
  if (!auth.token) return
  void fetchMyRating(auth.token)
    .then((payload) => {
      standing.value = payload.standing
    })
    .catch(() => undefined)
})

function onActiveDeckChange(event: Event): void {
  const id = (event.target as HTMLSelectElement).value
  const opt = deckOptions.value.find((row) => row.id === id)
  if (!opt?.playable) return
  deckStore.setActiveId(id)
}

const go = (query: Record<string, string>) => {
  DuelSfx.prime()
  armDuelLandscape()
  router.push({
    path: '/online-duel',
    query: { ...query, username: player.username },
  })
}

const quickMatch = () => {
  if (ranked.closed) return
  go({ mode: 'matchmaking' })
}

const casualMatch = () => {
  if (ranked.closed) return
  go({ mode: 'casual' })
}

const eventMatch = () => {
  if (!site.eventQueue) return
  go({ mode: 'event' })
}
</script>

<style scoped>
.lobby {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 24px;
  min-height: calc(100dvh - var(--app-nav-offset, 64px));
  padding: 24px 16px;
}

.lobby :deep(.block) {
  width: min(960px, 100%);
  box-sizing: border-box;
}

.lobby-title {
  margin: 0;
  color: var(--md-sys-color-primary);
  font-size: 36px;
  font-weight: 700;
  text-align: center;
}

.lobby-rank {
  display: flex;
  align-items: center;
  gap: 12px;
  color: var(--md-sys-color-on-surface);
  font-size: 16px;
  font-weight: 600;
  text-decoration: none;
}

.lobby-rank:hover {
  color: var(--md-sys-color-primary);
}

.lobby-rank p {
  margin: 0;
}

.deck-line {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  gap: 8px;
  color: var(--md-sys-color-on-surface-variant);
  font-size: 14px;
}

.deck-pick {
  display: inline-flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
}

.deck-select {
  min-height: 32px;
  padding: 0 12px;
  border-radius: 8px;
  border: 1px solid var(--md-sys-color-outline);
  background: var(--md-sys-color-surface-container-high);
  color: var(--md-sys-color-on-surface);
  font: inherit;
  font-size: 14px;
}

.deck-edit {
  margin-left: 8px;
  color: var(--md-sys-color-primary);
}

.deck-edit:hover,
.lobby-back:hover {
  color: var(--md-sys-color-on-surface);
}

.lobby-back {
  color: var(--md-sys-color-on-surface-variant);
  font-size: 14px;
}

.actions {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
}

.lobby-hint {
  margin: 0;
  color: var(--md-sys-color-on-surface-variant);
  font-size: 13px;
  font-weight: 400;
}

.lobby-offer {
  color: var(--md-sys-color-primary);
  font-weight: 700;
}

.btn-primary {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
  background: var(--md-sys-color-primary);
  color: var(--md-sys-color-on-primary);
  font-weight: 700;
  border-radius: 8px;
  padding: 12px 24px;
  min-height: 44px;
  cursor: pointer;
  border: 0;
  font-size: inherit;
  font-family: inherit;
}

.btn-primary:disabled {
  opacity: 0.55;
  cursor: default;
}

.btn-queue {
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.2px;
  opacity: 0.9;
}

.btn-primary:hover {
  filter: brightness(1.08);
}

.btn-casual {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
  min-height: 44px;
  padding: 12px 24px;
  border-radius: 8px;
  border: 1px solid var(--md-sys-color-outline);
  background: transparent;
  color: var(--md-sys-color-primary);
  font: inherit;
  font-weight: 700;
  cursor: pointer;
}

.btn-casual:disabled {
  opacity: 0.55;
  cursor: default;
}

.btn-casual:hover:not(:disabled) {
  background: var(--md-sys-color-surface-container-high);
}

.btn-secondary {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 44px;
  padding: 12px 24px;
  border-radius: 8px;
  border: 1px solid var(--md-sys-color-outline);
  background: transparent;
  color: var(--md-sys-color-primary);
  font: inherit;
  font-weight: 700;
  text-decoration: none;
}

.btn-secondary:hover {
  background: var(--md-sys-color-surface-container-high);
}

@media (max-width: 720px) {
  .lobby-title {
    font-size: 28px;
  }
}

@media (max-height: 500px) and (max-width: 1100px) {
  .lobby {
    justify-content: flex-start;
    gap: 12px;
    padding: 16px;
  }

  .lobby-title {
    font-size: 24px;
  }
}
</style>
